import { useEffect, useRef, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import {
  ArrowUpRight,
  Zap,
  Leaf,
  Cpu,
  Infinity as InfinityIcon,
  Sparkles,
  Send,
  Loader2,
  Check,
  AlertCircle,
} from "lucide-react";
import LogoShowcase from "./LogoShowcase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ————————————————————————————————————————————————————————————
   The X — the brand. ASCII Glyph: X built from monospace code chars
   (0, 1, /, \, x, *, ., +). Developer-first, terminal aesthetic.
   Sizes via `1em` so it scales with surrounding font-size.
———————————————————————————————————————————————————————————— */
const ASCII_CHARS = ["0", "1", "/", "\\", "x", "*", ".", "+"];
const BrandX = ({ scale = 1.4, className = "" }) => {
  const cells = [];
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const onX = r === c || r + c === 8;
      const near = Math.abs(r - c) === 1 || Math.abs(r + c - 8) === 1;
      if (onX || near) {
        cells.push({
          r,
          c,
          onX,
          ch: ASCII_CHARS[(r + c) % ASCII_CHARS.length],
        });
      }
    }
  }
  return (
    <span
      className={`brand-x ${className}`}
      style={{
        display: "inline-block",
        width: `${scale}em`,
        height: `${scale}em`,
        verticalAlign: "-0.25em",
        lineHeight: 0,
        margin: "0 0.05em",
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none">
        {cells.map((cell, i) => (
          <text
            key={i}
            x={10 + cell.c * 10}
            y={16 + cell.r * 10}
            fontFamily="JetBrains Mono, ui-monospace, monospace"
            fontSize="9"
            fontWeight="700"
            fill={cell.onX ? "#5ef0ff" : "#ffffff"}
            opacity={cell.onX ? 1 : 0.22}
            textAnchor="middle"
          >
            {cell.ch}
          </text>
        ))}
      </svg>
    </span>
  );
};

/* The piXcan wordmark — lowercase letters with the ASCII X centerpiece. */
const Wordmark = ({ className = "", weight = 600, xScale = 1.4 }) => (
  <span
    className={`font-display tracking-tight ${className}`}
    style={{ fontWeight: weight, letterSpacing: "-0.02em" }}
    data-testid="brand-wordmark"
  >
    <span className="text-white">pi</span>
    <BrandX scale={xScale} />
    <span className="text-white">can</span>
  </span>
);

/* Standalone logomark (the X by itself, for favicon-style use). */
// eslint-disable-next-line no-unused-vars
const Logomark = ({ size = 36 }) => {
  const cells = [];
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const onX = r === c || r + c === 8;
      const near = Math.abs(r - c) === 1 || Math.abs(r + c - 8) === 1;
      if (onX || near) {
        cells.push({
          r,
          c,
          onX,
          ch: ASCII_CHARS[(r + c) % ASCII_CHARS.length],
        });
      }
    }
  }
  return (
    <span
      style={{
        display: "inline-flex",
        width: size,
        height: size,
        lineHeight: 0,
      }}
      aria-label="piXcan logomark"
    >
      <svg viewBox="0 0 100 100" width={size} height={size} fill="none">
        {cells.map((cell, i) => (
          <text
            key={i}
            x={10 + cell.c * 10}
            y={16 + cell.r * 10}
            fontFamily="JetBrains Mono, monospace"
            fontSize="9"
            fontWeight="700"
            fill={cell.onX ? "#5ef0ff" : "#ffffff"}
            opacity={cell.onX ? 1 : 0.22}
            textAnchor="middle"
          >
            {cell.ch}
          </text>
        ))}
      </svg>
    </span>
  );
};

/* ————————————————————————————————————————————————————————————
   Reveal-on-scroll hook
———————————————————————————————————————————————————————————— */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ————————————————————————————————————————————————————————————
   Top navigation
———————————————————————————————————————————————————————————— */
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[#050510]/70 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-6">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center group"
        >
          <Wordmark className="text-5xl md:text-6xl" weight={600} xScale={1.25} />
        </a>

        <div className="hidden md:flex items-center gap-10 text-sm text-[color:var(--ink-2)]">
          <a
            data-testid="nav-solution"
            href="#solution"
            className="hover:text-white transition-colors"
          >
            Solution
          </a>
          <a
            data-testid="nav-technology"
            href="#technology"
            className="hover:text-white transition-colors"
          >
            Technology
          </a>
          <a
            data-testid="nav-contact"
            href="#contact"
            className="hover:text-white transition-colors"
          >
            Contact
          </a>
        </div>

        <a
          data-testid="nav-cta"
          href="#contact"
          className="btn-ghost text-sm"
        >
          Get in touch <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </nav>
  );
};

/* ————————————————————————————————————————————————————————————
   Hero
———————————————————————————————————————————————————————————— */
const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-28 pb-20"
    >
      {/* layered ambient background */}
      <div className="dot-matrix" />
      <div className="photon-grid" />
      <div
        className="light-beam"
        style={{ top: "32%", animationDelay: "0s" }}
      />
      <div
        className="light-beam"
        style={{ top: "58%", animationDelay: "-2s", opacity: 0.6 }}
      />
      <div
        className="light-beam"
        style={{ top: "78%", animationDelay: "-4s", opacity: 0.4 }}
      />

      <div className="relative max-w-[1400px] w-full mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
        {/* Left — headline */}
        <div className="md:col-span-7 relative">
          <div
            data-testid="hero-pill"
            className="inline-flex items-center gap-2 text-xs font-mono text-[color:var(--ink-2)] border border-white/10 rounded-full px-3 py-1.5 mb-8"
          >
            <span className="photon-dot" />
            <span>PHOTONIC AI ACCELERATION</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-[clamp(2.6rem,7vw,5.8rem)] leading-[0.98] font-medium text-white"
          >
            Computing at the
            <br />
            <span className="spectrum-text">speed of light.</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-8 text-lg md:text-xl text-[color:var(--ink-2)] max-w-xl leading-relaxed"
          >
            <Wordmark className="text-lg md:text-xl" weight={600} xScale={1.05} /> builds photonic accelerators for nanosecond-scale vector
            retrieval — removing the retrieval bottleneck in large-scale AI
            with dramatically lower energy per query.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              data-testid="hero-cta-primary"
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("contact");
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  // focus the name input shortly after scroll starts
                  setTimeout(() => {
                    const input = document.getElementById("cf-name");
                    if (input) input.focus({ preventScroll: true });
                  }, 700);
                }
              }}
              className="btn-primary"
            >
              Request early access
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              data-testid="hero-cta-secondary"
              href="#solution"
              className="btn-ghost"
            >
              See the solution
            </a>
          </div>

          {/* micro-metrics — no financials, only physics-style descriptors */}
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
            {[
              { k: "Latency", v: "ns-scale", sub: "optical interference" },
              { k: "Parallelism", v: "WDM", sub: "wavelength multiplexing" },
              { k: "Energy", v: "↓↓↓", sub: "vs. GPU baseline" },
            ].map((m, i) => (
              <div
                key={m.k}
                data-testid={`hero-metric-${i}`}
                className="reveal"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <div className="text-xs font-mono text-[color:var(--ink-3)] uppercase tracking-widest mb-2">
                  {m.k}
                </div>
                <div className="font-display text-2xl text-white">{m.v}</div>
                <div className="text-xs text-[color:var(--ink-2)] mt-1">
                  {m.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — animated photonic chip visual */}
        <div
          data-testid="hero-visual"
          className="md:col-span-5 relative h-[480px] hidden md:block"
        >
          <PhotonicVisual />
        </div>
      </div>

      {/* scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[color:var(--ink-3)] text-xs font-mono tracking-widest flex flex-col items-center gap-2">
        <span>SCROLL</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
};

/* ————————————————————————————————————————————————————————————
   Photonic chip SVG — vector beams animating through a lattice
———————————————————————————————————————————————————————————— */
const PhotonicVisual = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    {/* glowing ring */}
    <div
      className="absolute w-[420px] h-[420px] rounded-full"
      style={{
        background:
          "conic-gradient(from 0deg, rgba(255,110,231,0.18), rgba(168,139,255,0.18), rgba(94,240,255,0.18), rgba(166,255,77,0.18), rgba(255,110,231,0.18))",
        filter: "blur(30px)",
        animation: "spectrum-shift 12s linear infinite",
      }}
    />
    <svg
      viewBox="0 0 400 400"
      className="relative w-[420px] h-[420px]"
      fill="none"
    >
      {/* chip substrate */}
      <rect
        x="80"
        y="80"
        width="240"
        height="240"
        rx="18"
        stroke="rgba(255,255,255,0.18)"
        strokeWidth="1"
      />
      <rect
        x="100"
        y="100"
        width="200"
        height="200"
        rx="10"
        stroke="rgba(94,240,255,0.25)"
        strokeWidth="1"
        strokeDasharray="4 6"
      />

      {/* waveguide grid */}
      {Array.from({ length: 8 }).map((_, i) => (
        <line
          key={`h-${i}`}
          x1="100"
          x2="300"
          y1={120 + i * 22}
          y2={120 + i * 22}
          stroke="rgba(255,255,255,0.06)"
        />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <line
          key={`v-${i}`}
          y1="100"
          y2="300"
          x1={120 + i * 22}
          x2={120 + i * 22}
          stroke="rgba(255,255,255,0.06)"
        />
      ))}

      {/* photon beams drawing across */}
      <g strokeWidth="2" strokeLinecap="round">
        <line
          x1="100"
          y1="160"
          x2="300"
          y2="160"
          stroke="#5ef0ff"
          className="vec-beam"
          style={{ animationDelay: "0s" }}
        />
        <line
          x1="100"
          y1="220"
          x2="300"
          y2="220"
          stroke="#a88bff"
          className="vec-beam"
          style={{ animationDelay: "0.8s" }}
        />
        <line
          x1="100"
          y1="260"
          x2="300"
          y2="260"
          stroke="#ff6ee7"
          className="vec-beam"
          style={{ animationDelay: "1.6s" }}
        />
        <line
          x1="160"
          y1="100"
          x2="160"
          y2="300"
          stroke="#a6ff4d"
          className="vec-beam"
          style={{ animationDelay: "0.4s" }}
        />
        <line
          x1="240"
          y1="100"
          x2="240"
          y2="300"
          stroke="#ffb347"
          className="vec-beam"
          style={{ animationDelay: "1.2s" }}
        />
      </g>

      {/* interference nodes */}
      {[
        [160, 160],
        [240, 160],
        [160, 220],
        [240, 220],
        [200, 260],
      ].map(([cx, cy], i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r="4"
          fill="#fff"
          style={{
            filter: "drop-shadow(0 0 6px #5ef0ff)",
          }}
        />
      ))}

      {/* corner markers */}
      {[
        [80, 80],
        [320, 80],
        [80, 320],
        [320, 320],
      ].map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r="3" fill="#5ef0ff" />
        </g>
      ))}

      {/* label */}
      <text
        x="200"
        y="360"
        textAnchor="middle"
        fill="rgba(255,255,255,0.35)"
        fontSize="10"
        fontFamily="JetBrains Mono, monospace"
        letterSpacing="3"
      >
        PIC · VECTOR SIMILARITY ENGINE
      </text>
    </svg>
  </div>
);

/* ————————————————————————————————————————————————————————————
   Problem / Solution
———————————————————————————————————————————————————————————— */
const Solution = () => (
  <section
    id="solution"
    data-testid="solution-section"
    className="relative py-32 md:py-40 px-6 md:px-12"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4 md:sticky md:top-32 self-start">
          <div className="text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase mb-4 reveal">
            01 · The shift
          </div>
          <h2
            data-testid="solution-heading"
            className="font-display text-4xl md:text-5xl text-white leading-[1.05] reveal"
          >
            Retrieval is the{" "}
            <span className="spectrum-text">new bottleneck.</span>
          </h2>
          <p className="mt-6 text-[color:var(--ink-2)] text-lg leading-relaxed reveal">
            Modern AI — RAG, semantic search, recommendations, robotics — lives
            and dies by vector similarity search. On electronics, every query
            means millions of multiply–accumulate cycles. It is slow, it is
            hot, and it keeps getting worse as embeddings scale.
          </p>
        </div>

        <div className="md:col-span-8 space-y-6">
          {[
            {
              label: "Problem",
              title: "The electronic ceiling",
              body: "GPUs and CPUs brute-force vector similarity with billions of MAC operations per query. Latency climbs, energy and cooling spiral, and AI systems slow down exactly when they need to be faster.",
              accent: "#ff6ee7",
            },
            {
              label: "Solution",
              title: "Similarity computed in light",
              body: (
                <>
                  <Wordmark className="text-base md:text-lg" weight={600} xScale={1.05} /> performs vector similarity directly in the optical domain. Embeddings are encoded into light; interference in a photonic integrated circuit yields the dot-product at the speed it travels through the chip.
                </>
              ),
              accent: "#5ef0ff",
            },
            {
              label: "Outcome",
              title: "Nanoseconds, at a fraction of the energy",
              body: "A single photonic retrieval engine replaces racks of electronic accelerators for similarity workloads — turning a compute-bound bottleneck into a physics-bound advantage.",
              accent: "#a6ff4d",
            },
          ].map((c, i) => (
            <div
              key={c.label}
              data-testid={`solution-card-${i}`}
              className="glass rounded-2xl p-8 md:p-10 reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    background: c.accent,
                    boxShadow: `0 0 12px ${c.accent}`,
                  }}
                />
                <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--ink-2)]">
                  {c.label}
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white mb-4">
                {c.title}
              </h3>
              <p className="text-[color:var(--ink-2)] text-base md:text-lg leading-relaxed max-w-2xl">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ————————————————————————————————————————————————————————————
   Technology / USP grid
———————————————————————————————————————————————————————————— */
const Technology = () => {
  const cards = [
    {
      icon: Zap,
      title: "Nanosecond latency",
      body: "Computation happens as light propagates — retrieval in the time it takes a photon to cross the chip.",
      accent: "#5ef0ff",
    },
    {
      icon: InfinityIcon,
      title: "Massive parallelism",
      body: "Wavelength multiplexing lets many similarity computations run simultaneously on a single device.",
      accent: "#a88bff",
    },
    {
      icon: Leaf,
      title: "Dramatically lower energy",
      body: "No electronic multiply-accumulate. Lower power, lower cooling, lower carbon per query.",
      accent: "#a6ff4d",
    },
    {
      icon: Cpu,
      title: "No electronic rival",
      body: "Vector similarity via optical interference is a category of its own — GPUs and FPGAs can't replicate it.",
      accent: "#ff6ee7",
    },
  ];

  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="relative py-32 md:py-40 px-6 md:px-12 border-t border-white/5"
    >
      {/* ambient orbs */}
      <div className="dot-matrix" style={{ opacity: 0.5 }} />
      <div className="relative max-w-[1400px] mx-auto">
        <div className="max-w-3xl mb-16">
          <div className="text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase mb-4 reveal">
            02 · Why photonics
          </div>
          <h2
            data-testid="technology-heading"
            className="font-display text-4xl md:text-5xl text-white leading-[1.05] reveal"
          >
            A retrieval engine built from{" "}
            <span className="spectrum-text">physics</span>, not cycles.
          </h2>
          <p className="mt-6 text-[color:var(--ink-2)] text-lg leading-relaxed reveal">
            Four properties of light, engineered into silicon photonic
            integrated circuits.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`tech-card-${i}`}
                className="glass rounded-2xl p-8 md:p-10 reveal"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                  style={{
                    background: `linear-gradient(135deg, ${c.accent}22, transparent)`,
                    border: `1px solid ${c.accent}55`,
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: c.accent }} />
                </div>
                <h3 className="font-display text-2xl text-white mb-3">
                  {c.title}
                </h3>
                <p className="text-[color:var(--ink-2)] leading-relaxed">
                  {c.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* use cases strip */}
        <div className="mt-20 reveal">
          <div className="text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase mb-6">
            Built for
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              "RAG systems",
              "Semantic search",
              "Recommendation engines",
              "Vector databases",
              "Robotics & localization",
              "Autonomous systems",
              "AI infrastructure providers",
              "Hyperscale cloud",
            ].map((t) => (
              <span
                key={t}
                data-testid={`usecase-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-2 rounded-full border border-white/10 text-sm text-[color:var(--ink-1)] hover:border-[color:var(--cyan)] hover:text-white transition-colors cursor-default"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ————————————————————————————————————————————————————————————
   Mission quote strip
———————————————————————————————————————————————————————————— */
const MissionStrip = () => (
  <section
    data-testid="mission-section"
    className="relative py-32 md:py-40 px-6 md:px-12 overflow-hidden"
  >
    <div className="spectrum-line absolute top-0 left-0 right-0" />
    <div className="spectrum-line absolute bottom-0 left-0 right-0" />
    <div className="max-w-[1100px] mx-auto text-center">
      <Sparkles className="w-6 h-6 mx-auto text-[color:var(--cyan)] mb-6 reveal" />
      <p
        data-testid="mission-quote"
        className="font-display text-3xl md:text-5xl text-white leading-[1.15] reveal"
      >
        A world where AI retrieval is powered by{" "}
        <span className="spectrum-text">light</span>
        <span className="text-[color:var(--ink-2)]">
          {" "}
          — infinitely scalable, sustainable, and instant.
        </span>
      </p>
      <div className="mt-10 text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase reveal flex items-center justify-center gap-2">
        <Wordmark className="text-sm" weight={600} xScale={1.0} /> · Vision 2035
      </div>
    </div>
  </section>
);

/* ————————————————————————————————————————————————————————————
   Contact — form posts to /api/contact
———————————————————————————————————————————————————————————— */
const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setErrorMsg("All fields are required.");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form, {
        headers: { "Content-Type": "application/json" },
        timeout: 15000,
      });
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrorMsg(typeof detail === "string" ? detail : "Submission failed.");
    }
  };

  const sending = status === "sending";

  return (
    <form
      data-testid="contact-form"
      onSubmit={onSubmit}
      className="glass rounded-2xl p-7 md:p-8 reveal space-y-4"
      noValidate
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="photon-dot" />
        <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--ink-2)]">
          Send us a message
        </span>
      </div>

      <div>
        <label
          htmlFor="cf-name"
          className="block text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mb-2"
        >
          Name
        </label>
        <input
          id="cf-name"
          data-testid="cf-name"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={update("name")}
          disabled={sending}
          maxLength={120}
          className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--cyan)] outline-none text-white text-base py-2 transition-colors placeholder:text-[color:var(--ink-3)]"
          placeholder="Pink Floyd"
          required
        />
      </div>

      <div>
        <label
          htmlFor="cf-email"
          className="block text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mb-2"
        >
          Email
        </label>
        <input
          id="cf-email"
          data-testid="cf-email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={update("email")}
          disabled={sending}
          maxLength={200}
          className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--cyan)] outline-none text-white text-base py-2 transition-colors placeholder:text-[color:var(--ink-3)]"
          placeholder="you@company.com"
          required
        />
      </div>

      <div>
        <label
          htmlFor="cf-message"
          className="block text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mb-2"
        >
          Message
        </label>
        <textarea
          id="cf-message"
          data-testid="cf-message"
          rows={5}
          value={form.message}
          onChange={update("message")}
          disabled={sending}
          maxLength={5000}
          className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--cyan)] outline-none text-white text-base py-2 transition-colors resize-none placeholder:text-[color:var(--ink-3)]"
          placeholder="Tell us what you're building, what you'd like to discuss, or what you need."
          required
        />
      </div>

      <div className="pt-3 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
        <button
          type="submit"
          data-testid="cf-submit"
          disabled={sending}
          className="btn-primary disabled:opacity-60 disabled:cursor-wait"
        >
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Send message
              <Send className="w-4 h-4" />
            </>
          )}
        </button>

        {status === "success" && (
          <div
            data-testid="cf-success"
            className="flex items-center gap-2 text-sm text-[color:var(--lime,#a6ff4d)]"
          >
            <Check className="w-4 h-4" />
            Message sent — we'll be in touch shortly.
          </div>
        )}
        {status === "error" && (
          <div
            data-testid="cf-error"
            className="flex items-center gap-2 text-sm text-[color:var(--magenta,#ff6ee7)]"
          >
            <AlertCircle className="w-4 h-4" />
            {errorMsg}
          </div>
        )}
      </div>
    </form>
  );
};

const Contact = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="relative py-32 md:py-40 px-6 md:px-12"
  >
    <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-6">
        <div className="text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase mb-4 reveal">
          03 · Talk to us
        </div>
        <h2
          data-testid="contact-heading"
          className="font-display text-5xl md:text-7xl text-white leading-[0.95] reveal"
        >
          Let's rebuild AI <br />
          <span className="spectrum-text">at the speed of light.</span>
        </h2>
        <p className="mt-8 text-lg text-[color:var(--ink-2)] max-w-xl reveal">
          We partner with AI infrastructure teams, vector-database platforms,
          and deep-tech investors. Early access, pilot programs, research
          collaborations — reach out.
        </p>
      </div>

      <div className="md:col-span-6">
        <ContactForm />
      </div>
    </div>
  </section>
);

/* ————————————————————————————————————————————————————————————
   Footer
———————————————————————————————————————————————————————————— */
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative border-t border-white/5 py-10 px-6 md:px-12"
  >
    <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <Wordmark className="text-sm" weight={600} xScale={1.0} />
      </div>
      <div className="text-xs font-mono text-[color:var(--ink-3)] tracking-widest uppercase">
        © {new Date().getFullYear()} · Computing at the speed of light
      </div>
      <a
        data-testid="footer-contact-link"
        href="#contact"
        className="text-sm text-[color:var(--ink-2)] hover:text-white transition-colors"
      >
        Contact
      </a>
    </div>
  </footer>
);

/* ————————————————————————————————————————————————————————————
   App shell
———————————————————————————————————————————————————————————— */
const Home = () => {
  useReveal();
  return (
    <div className="App" data-testid="app-root">
      <div className="grain" />
      <Nav />
      <main>
        <Hero />
        <Solution />
        <Technology />
        <MissionStrip />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/logos" element={<LogoShowcase />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
